import { FormEvent, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import { AdminUser, loginAdmin } from '../lib_api';

type Props = { onLoggedIn: (user: AdminUser) => Promise<void> | void };

export default function AdminLogin({ onLoggedIn }: Props) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await loginAdmin(email.trim(), password);
      if (!['admin', 'superadmin'].includes(String(user.role))) {
        toast.error('This account does not have admin access.');
        return;
      }
      if (import.meta.env.DEV) {
        console.info('[admin-auth] login succeeded', { email: user.email, role: user.role });
      }
      toast.success('Signed in to Pickaxe Admin.');
      await onLoggedIn(user);
      if (import.meta.env.DEV) {
        console.info('[admin-auth] navigating to dashboard after login');
      }
      navigate('/', { replace: true });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-softGray px-3 py-6 sm:px-4 dark:bg-darkBg">
      <div className="panel w-full max-w-md p-5 sm:p-7">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-mutedGray dark:text-darkTextSecondary">Staff Access</p>
            <h1 className="mt-1 text-2xl font-semibold text-darkText dark:text-darkTextPrimary">Pickaxe Admin Console</h1>
          </div>
          <ThemeToggle />
        </div>

        <div className="mb-6 rounded-lg bg-indigo-50 p-3 text-indigo-700 dark:bg-indigo-600/20 dark:text-indigo-200">
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-4 w-4" />
            Internal operations dashboard
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <input className="input" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="input" placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button className="btn w-full" type="submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
